<script lang="ts" setup>
import { languages } from "@/plugins/i18n";
import { useLocale } from "vuetify"; // TODO: OBSOLETE.
import { onMounted, onBeforeUnmount, ref } from "vue";

const { current, t } = useLocale();
const mailApiBaseUrl = import.meta.env.VITE_MAIL_API_BASE_URL?.replace(/\/+$/, "");

const links =
[
  {
    href: "siteHeader.links.aboutLink",
    text: "siteHeader.links.about",
  },
  {
    href: "siteHeader.links.strategyLink",
    text: "siteHeader.links.strategy",
  },
  {
    href: "siteHeader.links.portfolioLink",
    text: "siteHeader.links.portfolio",
   },
   {
    href: "siteHeader.links.successesLink",
    text: "siteHeader.links.successes",
    isDropdown: true,
    items: [
      {
        href: "siteHeader.links.llsLink",
        text: "siteHeader.links.lls",
      },
      {
        href: "siteHeader.links.aggrekoLink",
        text: "siteHeader.links.aggreko",
      }
    ]
   },
  {
    href: "siteHeader.links.teamLink",
    text: "siteHeader.links.team",
   },
  // {
  //   href: "siteHeader.links.newsLink",
  //   text: "siteHeader.links.news",
  // },
  // {
  //   href: "siteHeader.links.downloadsLink",
  //   text: "siteHeader.links.downloads",
  // },
  {
    href: "siteHeader.links.contactLink",
    text: "siteHeader.links.contact",
  },
];

// Investor Portal modal state
const investorPortalShow = ref(false);
const showRegistrationForm = ref(false);
const showThankYouModal = ref(false);

// Form data
const formData = ref({
  firstName: '',
  lastName: '',
  organization: '',
  jobTitle: '',
  emailAddress: ''
});

const handleRegistrationSubmit = async () => {
  try {
    // Validate required fields
    if (!formData.value.firstName || !formData.value.lastName || !formData.value.emailAddress) {
      alert('Please fill in all required fields (First Name, Last Name, and Email Address)');
      return;
    }

    if (!mailApiBaseUrl) {
      throw new Error("Mail API endpoint is not configured");
    }

    const res = await fetch(`${mailApiBaseUrl}/api/portal-registration`, {
      body: JSON.stringify({
        emailAddress: formData.value.emailAddress,
        firstName: formData.value.firstName,
        jobTitle: formData.value.jobTitle,
        lastName: formData.value.lastName,
        organization: formData.value.organization,
      }),
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
    });

    if (!res.ok) {
      const errorResponse = await res.json().catch(() => ({}));
      const errorMessage = typeof errorResponse.error === "string"
        ? errorResponse.error
        : `HTTP ${res.status}`;
      throw new Error(errorMessage);
    }

    // Reset form and close modals
    formData.value = {
      firstName: '',
      lastName: '',
      organization: '',
      jobTitle: '',
      emailAddress: ''
    };
    showRegistrationForm.value = false;
    investorPortalShow.value = false;
    
    // Show thank you modal
    showThankYouModal.value = true;
    
  } catch (error) {
    const message = error instanceof Error ? error.message : "Unexpected error";
    alert(`There was a problem submitting the registration: ${message}`);
  }
};

const showNavBar = ref(true);
const showDrawer = ref(false);

const wipList = ref<string[]>([]);
const wipShow = ref(false);

let lastScrollY = -1;



const onScroll = () =>
{
  const scrollY = window.scrollY || document.documentElement.scrollTop;

  // Ignore scroll momentum on mobile.
  if (scrollY < 0)
  {
    return;
  }

  // Ignore if less than threshold.
  if (Math.abs(scrollY - lastScrollY) < 200)
  {
    return;
  }

  showNavBar.value = scrollY < lastScrollY;
  lastScrollY = scrollY;
};

const onResize = () => {
  if (showDrawer.value) {
    showDrawer.value = false;
  }
};

onMounted(() =>
{
  window.addEventListener("scroll", onScroll);
  window.addEventListener("resize", onResize);
});

onBeforeUnmount(() =>
{
  window.removeEventListener("scroll", onScroll);
  window.addEventListener("resize", onResize);
});

const sanitizeClassName = (text: string) => {
  return t(text).toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
};
</script>

<template>
  <div class="h-24 lg:h-20 bg-white"></div>
  <header
    class="
      flex items-center
      w-full h-24 px-6 lg:h-20 transition-[top]
      fixed z-[10000]
      backdrop-blur bg-white/90
      border-b border-black/30"
    :class='{
      "top-0": (showNavBar || showDrawer),
      "max-lg:-top-24": !(showNavBar || showDrawer) }'>
    <img
      alt="LH Severus logo"
      class="py-2 h-full"
      src="/images/lh-severus-logo.svg">
    <!-- narrow display -->
    <div class="flex xl:hidden grow">
      <div class="grow"></div>
      <s-menu-button @click="showDrawer = !showDrawer" :toggle="showDrawer"></s-menu-button>
    </div>
    <!-- wide display -->
    <div class="hidden xl:flex grow items-center">
      <div class="mx-6">
        <button
          v-for="language in languages.filter(l => l.code !== current)"
          class="
            text-white bg-black h-12 px-4 py-2 mx-0.5 rounded-sm
            hover:!bg-severus-blue transition-[background-color]"
          :class='`
            lang-${language.code}
            ${language.code === "ar" ? "text-2xl" : ""}`'
          @click='() =>
          {
            current = language.code;
            if (wipList.includes(language.code))
            {
              wipShow = true;
            }
          }'
          :key="language.code">
          {{ language.name }}
        </button>
      </div>
      <div class="grow"></div>
      <!-- Desktop Navigation -->
      <nav
        :class='current === "ar"
          ? "[&>:not(:first-child)]:border-r"
          : "[&>:not(:first-child)]:border-l"'>
        <a
          :class="[
            'swipe px-5 border-black/30',
            'before:bg-severus-blue',
            'hover:text-white transition-colors',
            'group',
            'relative',
            sanitizeClassName(link.text)
          ]"
          :href="`#${t(link.href)}`"
          :key="link.href"
          v-for="link in links">
          <span class="top-level">
            {{ t(link.text) }}
            <!-- Drop down menu -->
            <span v-if="link.items" 
                class="overflow-hidden absolute top-full left-0 min-w-[141px] -ml-[1px] 
                    transition-[max-height] duration-300 ease-in-out max-h-0 
                    group-hover:duration-1000 group-hover:max-h-[120px] group-hover:-ml-[21px]">
                <div class="gradient-border">
                    <div class="w-full bg-[#164675] -mt-[1px] transition-colors duration-1000">
                        <a v-for="item in link.items" 
                            :href="`#${t(item.href)}`"
                            :class="[
                              'block', 
                              'text-center', 
                              'pt-1', 
                              'pb-1', 
                              'hover:bg-[#826724]', 
                              'text-white', 
                              'transition-colors', 
                              'duration-1000',
                              sanitizeClassName(item.text)
                            ]">
                            {{ t(item.text) }}
                        </a>
                    </div>
                </div>
            </span>
          </span>
        </a>
        <!-- Investor Portal Button -->
        <button
          :class="[
            'px-5 border-black/30',
            'hover:text-white transition-colors',
            'cursor-pointer',
            'investor-portal-btn' 
          ]"
          @click="investorPortalShow = true">
          <span class="top-level">
            {{ t("portfolioCompanies.investorPortal") }}
          </span>
        </button>
      </nav>
      <!-- End of desktop Navigation -->
    </div>
  </header>
  <div
    class="
      w-full h-full transition-[top] pt-24 lg:pt-20
      fixed z-[1000]"
    :class='{
      "top-0": showDrawer,
      "top-full": !showDrawer}'>
    <div
      class="w-full h-full backdrop-blur bg-white/90 overflow-auto">
      <div class="flex justify-center m-4">
        <button
          class="
          text-white bg-black text-2xl hover:!bg-severus-blue transition-[background-color]
            px-14 py-3 m-1 font-light rounded-sm"
          :class='`
            lang-${language.code}
            ${language.code === "ar" ? "text-3xl" : ""}`'
          @click='() =>
          {
            current = language.code;
            showDrawer = false;
            if (wipList.includes(language.code))
            {
              wipShow = true;
            }
          }'
          :key="language.code"
          v-for="language in languages.filter(l => l.code !== current)">
          {{ language.name }}
        </button>
      </div>
      <!-- Mobile Navigation -->
      <nav class="my-8">
        <a
          class="
            w-full block text-center p-4 text-2xl font-light
            hover:bg-black/10 target:italic"
          @click="() => { showDrawer = false; }"
          :href="`#${t(link.href)}`"
          :key="link.href"
          v-for="link in links">
          {{ t(link.text) }}
      </a>
        <!-- Investor Portal Button -->
        <button
          class="
            w-full block text-center p-4 text-2xl font-light
            hover:bg-black/10 cursor-pointer"
          @click="() => { showDrawer = false; investorPortalShow = true; }">
          {{ t("portfolioCompanies.investorPortal") }}
        </button>
      </nav>
      <!-- End of mobile Navigation -->
    </div>
  </div>
  <v-dialog v-model="wipShow">
    <div
      class="construction"
      :class='{ "text-right": current === "ar" }'>
      <div class="lang-ar" v-if='current === "ar"'>
        الموقع العربي قيد التقدم. أرجوك عد قريبا.
      </div>
      <div class="lang-it" v-if='current === "it"'>
        Il sito italiano è un work in progress.<br>Per favore torna presto.
      </div>
      <v-btn class="mt-8" @click="wipShow = false" color="black">
        <div class="lang-ar" v-if='current === "ar"'>
          رفض
        </div>
        <div class="lang-it" v-if='current === "it"'>
          Congedare
        </div>
      </v-btn>
    </div>
  </v-dialog>

  <!-- Investor Portal Modal -->
  <v-dialog v-model="investorPortalShow" max-width="700" :fullscreen="$vuetify.display.mdAndDown">
    <div class="investor-portal-modal investor-portal-modal-first">
      <!-- Header with logo and company name -->
      <div class="bg-white">
        <div class="flex justify-end">
          <v-btn icon="mdi-close" variant="text" @click="investorPortalShow = false" size="x-large" class="text-grey close-btn mobile-close-margin"></v-btn>
        </div>
        <div class="flex items-center justify-center pb-8">
          <div class="w-128 h-32 -ml-10 hidden md:block md:mt-8">
            <img src="/images/logos/lhs_document_logo.svg" alt="LH Severus Document Logo" class="w-full h-full">
          </div>
          <div class="h-[190px] block md:hidden">
            <img src="/images/logos/lhs-document-logo-mobile.svg" alt="LH Severus Document Logo" class="w-full h-full">
          </div>
        </div>
      </div>
      
      <!-- Action buttons -->
      <div class="px-12 md:px-24 portal-bg action-buttons">
        <div class="flex flex-col gap-6 modal-buttons">
          <v-btn 
            block             
            class="swipe !text-severus-blue hover:!text-white !rounded-none before:bg-black before:rounded-none !text-xl !font-semibold !shadow-none"
            :href="'https://lhsdocuments.com/'"
            target="_blank"
            rel="noopener noreferrer"
            height="50">
            {{ t("portfolioCompanies.investorPortalModal.signIn") }}
          </v-btn>
          <v-btn 
            block             
            class="swipe !text-severus-blue hover:!text-white !rounded-none before:bg-black before:rounded-none !text-xl !font-semibold !shadow-none"
            @click="showRegistrationForm = true"
            height="50">
            {{ t("portfolioCompanies.investorPortalModal.registerNewAccount") }}
          </v-btn>          
        </div>
      </div>
    </div>
  </v-dialog>
  
        <!-- Registration Form Dialog -->
  <v-dialog v-model="showRegistrationForm" max-width="700" :fullscreen="$vuetify.display.mdAndDown">
    <div class="investor-portal-modal">
      <!-- Header with logo and company name -->
      <div class="bg-white">
        <div class="flex justify-end">
          <v-btn icon="mdi-close" variant="text" @click="showRegistrationForm = false" size="x-large" class="text-grey close-btn mobile-close-margin"></v-btn>
        </div>
        <div class="flex items-center justify-center pb-8">
          <div class="w-128 h-32 -ml-10 hidden md:block md:mt-8">
            <img src="/images/logos/lhs_document_logo.svg" alt="LH Severus Document Logo" class="w-full h-full">
          </div>
          <div class="h-[190px] block md:hidden">
            <img src="/images/logos/lhs-document-logo-mobile.svg" alt="LH Severus Document Logo" class="w-full h-full">
          </div>
        </div>
      </div>
      
      <!-- Registration Form -->
      <div class="px-16 portal-bg registration-form">
        <h3 class="text-white font-semibold text-2xl mb-4 text-center">
          {{ t("portfolioCompanies.investorPortalModal.registerTitle") }}
        </h3>
        
        <p class="text-white text-sm mb-8 text-center">
          {{ t("portfolioCompanies.investorPortalModal.registerInstructions") }}
        </p>
        
        <form @submit.prevent="handleRegistrationSubmit">
          <v-text-field
            v-model="formData.firstName"
            :placeholder="t('portfolioCompanies.investorPortalModal.firstName')"
            variant="outlined"
            bg-color="white"
            density="compact"
            class="!rounded-none -mb-3"
            required>
          </v-text-field>
          
          <v-text-field
            v-model="formData.lastName"
            :placeholder="t('portfolioCompanies.investorPortalModal.lastName')"
            variant="outlined"
            bg-color="white"
            density="compact"
            class="!rounded-none -mb-3"
            required>
          </v-text-field>
          
          <v-text-field
            v-model="formData.organization"
            :placeholder="t('portfolioCompanies.investorPortalModal.organization')"
            variant="outlined"
            bg-color="white"
            density="compact"
            class="!rounded-none -mb-3">
          </v-text-field>
          
          <v-text-field
            v-model="formData.jobTitle"
            :placeholder="t('portfolioCompanies.investorPortalModal.jobTitle')"
            variant="outlined"
            bg-color="white"
            density="compact"
            class="!rounded-none -mb-3">
          </v-text-field>
          
          <v-text-field
            v-model="formData.emailAddress"
            :placeholder="t('portfolioCompanies.investorPortalModal.emailAddress')"
            variant="outlined"
            bg-color="white"
            type="email"
            density="compact"
            class="mb-3 !rounded-none"
            required>
          </v-text-field>
          
          <v-btn
            type="submit"
            block
            variant="outlined"
            class="swipe text-white border-white !rounded-none before:bg-black before:rounded-none !text-xl lhportal-submit-btn"
            height="50">
            {{ t("portfolioCompanies.investorPortalModal.submit") }}
          </v-btn>
        </form>
      </div>
    </div>
  </v-dialog>
  
      <!-- Thank You Modal -->
  <v-dialog v-model="showThankYouModal" max-width="700" :fullscreen="$vuetify.display.mdAndDown">
    <div class="investor-portal-modal">
      <!-- Header with logo and company name -->
      <div class="bg-white">
        <div class="flex justify-end">
          <v-btn icon="mdi-close" variant="text" @click="showThankYouModal = false" size="x-large" class="text-grey close-btn mobile-close-margin"></v-btn>
        </div>
        <div class="flex items-center justify-center pb-8">
          <div class="w-128 h-32 -ml-10 hidden md:block md:mt-8">
            <img src="/images/logos/lhs_document_logo.svg" alt="LH Severus Document Logo" class="w-full h-full">
          </div>
          <div class="h-[190px] block md:hidden md:mt-8">
            <img src="/images/logos/lhs-document-logo-mobile.svg" alt="LH Severus Document Logo" class="w-full h-full">
          </div>
        </div>
      </div>
      
      <!-- Thank You Content -->
      <div class="px-12 pt-12 md:pt-24 portal-bg">
        <h3 class="text-white font-semibold text-lg mb-4 text-center !text-2xl thank-you-title">
          {{ t("portfolioCompanies.investorPortalModal.thankYouTitle") }}
        </h3>
        
        <p class="text-white text-md mb-10 md:mb-16 text-center">
          {{ t("portfolioCompanies.investorPortalModal.thankYouMessage") }}
        </p>
        
        <div class="text-center">
          <v-btn
            @click="showThankYouModal = false"
            block
            variant="outlined"
            class="swipe text-white border-white !rounded-none before:bg-black before:rounded-none !text-xl lhportal-close-btn"
            height="50">
            {{ t("portfolioCompanies.investorPortalModal.close") }}
          </v-btn>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<style>
  .gradient-border {
    background: linear-gradient(to bottom, #164675 0px, rgb(156 163 175) 1px, white);
    padding: 1px;
  }

  /* Close button styling */
  .close-btn .v-icon {
    color: #b4b4b4 !important;
    font-size: 24px !important;
  }

  .close-btn {
    padding: 8px !important;
  }

  /* Investor Portal button styling */
  .investor-portal-btn {
    border: 1px solid rgba(0, 0, 0, 0.3) !important;
    position: relative;
    overflow: hidden;
  }

  .investor-portal-btn:hover .investor-portal-icon {
    filter: invert(1);
  }

  /* Ensure text becomes white on hover */
  .investor-portal-btn:hover {
    color: white !important;
  }

  .investor-portal-btn:hover span {
    color: white !important;
  }

  /* Fix the before pseudo-element positioning */
  .investor-portal-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background-color: #164675;
    transition: left 0.3s ease;
    z-index: -1;
  }

  .investor-portal-btn:hover::before {
    left: 0;
  }

  /* Fix Vuetify button flex display issues */
  .investor-portal-modal .v-btn--block {
    display: block !important;
  }

  .investor-portal-modal .v-btn--block .v-btn__content {
    justify-content: center !important;
    text-align: center !important;
    align-items: center !important;
    height: 100% !important;
  }

  /* Ensure both buttons have consistent height and alignment */
  .investor-portal-modal .v-btn {
    align-items: center !important;
    justify-content: center !important;
  }
  
  .lhportal-submit-btn {
    display: block !important;
    margin-bottom: 35px !important;
  }

  .lhportal-close-btn {
    display: block !important;
    margin-bottom: 60px !important;
  }

  .investor-portal-modal-first .portal-bg {
    padding: 80px 0 !important;
  }

  .registration-form {
    padding: 32px 0 !important;
  }

  @media (max-width: 768px) {
    .investor-portal-modal-first .portal-bg {
      padding: 66px 20px !important;
    }

    .thank-you-title {
      padding: 0 24px !important;
    }

    .lhportal-submit-btn, .lhportal-close-btn {      
      margin-bottom: 160px !important;
    }
  }

  /* Ensure responsive padding works */
  @media (min-width: 768px) {
    .investor-portal-modal .portal-bg {
      padding-left: 6rem !important;
      padding-right: 6rem !important;
    }
  }

  .mobile-close-margin {      
      position: absolute !important; 
  }

  /* Mobile close button margin */
  @media (max-width: 767px) {
    .mobile-close-margin {      
      position: relative !important;
      top: 12px;
    }
  }

  .investor-portal-modal {
    background-image: url('/images/general/portal-background.webp') !important;
    background-position: center bottom !important;
    background-size: cover !important;
    background-repeat: no-repeat !important;
    background-color: #164675 !important;
  }

  /* Remove bottom gap on mobile */
  @media (max-width: 767px) {
    .investor-portal-modal {
      min-height: calc(100vh - 64px) !important;
      height: calc(100vh - 64px) !important;
      display: flex !important;
      flex-direction: column !important;
      margin-top: 64px !important;
      overflow-y: auto !important;      
    }
    
    .investor-portal-modal .portal-bg {
      flex: 1 !important;
      display: flex !important;
      flex-direction: column !important;
      min-height: 0 !important;
      padding-bottom: 20px !important;
    }
  }


</style>